import { useState, useEffect, useCallback } from 'react';
import { loyaltyRewardsService, appOffersService, appAnnouncementsService } from '../lib/database';
import { getDataSource } from '../lib/config';
import { initialRewards } from '../data/rewards';
import type { LoyaltyReward, AppOffer, AppAnnouncement } from '../types';

export function useLoyaltyRewards(enabled = true) {
  const [rewards, setRewards] = useState<LoyaltyReward[]>(initialRewards);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (getDataSource() === 'mock') {
      setRewards(initialRewards);
      return;
    }
    setLoading(true);
    try {
      const data = await loyaltyRewardsService.getAll();
      setRewards(data.length > 0 ? data : initialRewards);
    } catch (err) {
      console.error('useLoyaltyRewards', err);
      setRewards(initialRewards);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    void refresh();
  }, [enabled, refresh]);

  const save = useCallback(async (reward: LoyaltyReward) => {
    if (getDataSource() === 'mock') {
      setRewards((prev) => {
        const exists = prev.some((r) => r.id === reward.id);
        if (exists) return prev.map((r) => (r.id === reward.id ? reward : r));
        return [...prev, { ...reward, id: Date.now() }];
      });
      return;
    }
    const saved = await loyaltyRewardsService.save(reward);
    setRewards((prev) => {
      const exists = prev.some((r) => r.id === saved.id);
      if (exists) return prev.map((r) => (r.id === saved.id ? saved : r));
      return [...prev, saved];
    });
  }, []);

  const remove = useCallback(async (rewardId: number) => {
    if (getDataSource() !== 'mock') {
      await loyaltyRewardsService.delete(rewardId);
    }
    setRewards((prev) => prev.filter((r) => r.id !== rewardId));
  }, []);

  return { rewards, loading, refresh, save, remove };
}

export function useAppOffers(enabled = true) {
  const [offers, setOffers] = useState<AppOffer[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (getDataSource() === 'mock') {
      setOffers([]);
      return;
    }
    setLoading(true);
    try {
      setOffers(await appOffersService.getAll());
    } catch (err) {
      console.error('useAppOffers', err);
      setOffers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    void refresh();
  }, [enabled, refresh]);

  const save = useCallback(async (offer: AppOffer) => {
    if (getDataSource() === 'mock') {
      setOffers((prev) => {
        const exists = prev.some((o) => o.id === offer.id);
        if (exists) return prev.map((o) => (o.id === offer.id ? offer : o));
        return [...prev, { ...offer, id: `offer-${Date.now()}` }];
      });
      return;
    }
    const saved = await appOffersService.save(offer);
    setOffers((prev) => {
      const exists = prev.some((o) => o.id === saved.id);
      if (exists) return prev.map((o) => (o.id === saved.id ? saved : o));
      return [...prev, saved];
    });
  }, []);

  const remove = useCallback(async (offerId: string) => {
    if (getDataSource() !== 'mock') {
      await appOffersService.delete(offerId);
    }
    setOffers((prev) => prev.filter((o) => o.id !== offerId));
  }, []);

  return { offers, loading, refresh, save, remove };
}

export function useAppAnnouncements(enabled = true) {
  const [announcements, setAnnouncements] = useState<AppAnnouncement[]>([]);

  const refresh = useCallback(async () => {
    if (getDataSource() === 'mock') {
      setAnnouncements([]);
      return;
    }
    try {
      setAnnouncements(await appAnnouncementsService.getAll());
    } catch (err) {
      console.error('useAppAnnouncements', err);
      setAnnouncements([]);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    void refresh();
  }, [enabled, refresh]);

  const save = useCallback(async (row: AppAnnouncement) => {
    if (getDataSource() === 'mock') {
      setAnnouncements((prev) => [...prev.filter((a) => a.id !== row.id), row]);
      return;
    }
    const saved = await appAnnouncementsService.save(row);
    setAnnouncements((prev) => {
      const exists = prev.some((a) => a.id === saved.id);
      if (exists) return prev.map((a) => (a.id === saved.id ? saved : a));
      return [...prev, saved].sort((a, b) => a.sortOrder - b.sortOrder);
    });
  }, []);

  const remove = useCallback(async (id: string) => {
    if (getDataSource() !== 'mock') {
      await appAnnouncementsService.delete(id);
    }
    setAnnouncements((prev) => prev.filter((a) => a.id !== id));
  }, []);

  return { announcements, refresh, save, remove };
}
